import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Copy, ExternalLink, Loader2, QrCode, X, Download } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";

/**
 * LandingPagesList — the ambassador-attributed landing-page URL table.
 *
 * Fetches `GET /api/ambassador/landing-pages` (short link + Seekers + 17
 * niches + Home + Elders + Ambassadors — 22 rows total, all pre-tagged
 * with the current user's referral code). Renders each row with a Copy
 * and an Open button.
 *
 * Rendered on both:
 *   - /ambassador (Back Office → Landing Pages tab)
 *   - /ambassador/dashboard (Dashboard block)
 *
 * so the same code path drives both surfaces and the URLs never drift.
 */
export default function LandingPagesList({ compact = false }) {
  const [rows, setRows] = useState(null);
  const [qrFor, setQrFor] = useState(null); // { url, label }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/ambassador/landing-pages");
        if (!cancelled) setRows(data.pages);
      } catch {
        if (!cancelled) toast.error("Failed to load landing pages");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const copy = (u) => {
    navigator.clipboard.writeText(u);
    toast.success("Copied");
  };

  const downloadQr = () => {
    // Grab the rendered <svg> from the modal, serialise it, and hand it to
    // the browser as an SVG download. Vector so ambassadors can print it
    // as big as they need without pixelation.
    try {
      const svg = document.querySelector('[data-qr-svg]');
      if (!svg) return;
      const xml = new XMLSerializer().serializeToString(svg);
      const blob = new Blob([xml], { type: "image/svg+xml" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `cauldron-qr-${(qrFor.label || "share").toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 32)}.svg`;
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success("QR downloaded");
    } catch (e) {
      toast.error("Couldn't save QR — try screenshotting instead");
    }
  };

  if (!rows) {
    return (
      <div className="text-sm text-foreground/60" data-testid="landing-pages-loading">
        <Loader2 className="w-4 h-4 inline animate-spin mr-2" />Loading…
      </div>
    );
  }

  return (
    <>
    <div className={compact ? "space-y-1.5" : "space-y-2"} data-testid="landing-pages-list">
      {rows.map((p) => (
        <div
          key={p.slug}
          className={`parchment flex items-center gap-3 ${compact ? "p-3" : "p-4"}`}
          data-testid={`landing-row-${p.slug}`}
        >
          <div className="flex-1 min-w-0">
            <div className={`${compact ? "text-sm" : "font-serif text-lg"} truncate`}>{p.label}</div>
            <div className="text-[11px] font-mono text-foreground/55 truncate">{p.shareUrl}</div>
          </div>
          <button
            onClick={() => copy(p.shareUrl)}
            className="border border-secondary/40 hover:bg-secondary/10 px-3 py-2 rounded-sm inline-flex items-center gap-1.5 text-xs shrink-0"
            data-testid={`landing-copy-${p.slug}`}
            aria-label={`Copy ${p.label} link`}
          >
            <Copy className="w-3.5 h-3.5" /> Copy
          </button>
          <button
            onClick={() => setQrFor({ url: p.shareUrl, label: p.label })}
            className="border border-secondary/40 hover:bg-secondary/10 px-3 py-2 rounded-sm inline-flex items-center gap-1.5 text-xs shrink-0"
            data-testid={`landing-qr-${p.slug}`}
            aria-label={`Show QR for ${p.label}`}
          >
            <QrCode className="w-3.5 h-3.5" /> QR
          </button>
          <a
            href={p.shareUrl}
            target="_blank"
            rel="noreferrer"
            className="border border-secondary/40 hover:bg-secondary/10 px-3 py-2 rounded-sm inline-flex items-center gap-1.5 text-xs shrink-0"
            data-testid={`landing-open-${p.slug}`}
            aria-label={`Open ${p.label}`}
          >
            <ExternalLink className="w-3.5 h-3.5" /> Open
          </a>
        </div>
      ))}
    </div>

    {/* QR modal — SVG so downloads scale to any print size */}
    {qrFor && (
      <div
        className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={() => setQrFor(null)}
        data-testid="landing-qr-modal"
      >
        <div
          className="parchment p-8 max-w-md w-full space-y-5 text-center"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-start justify-between">
            <div className="text-left">
              <div className="label-mystic text-secondary">Share by QR</div>
              <div className="font-serif text-2xl mt-1">{qrFor.label}</div>
            </div>
            <button
              onClick={() => setQrFor(null)}
              className="text-foreground/60 hover:text-foreground"
              data-testid="landing-qr-close"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="bg-white p-6 rounded-sm inline-block mx-auto">
            <QRCodeSVG
              value={qrFor.url}
              size={240}
              level="M"
              includeMargin
              data-qr-svg=""
            />
          </div>
          <div className="text-[11px] font-mono text-foreground/55 break-all">{qrFor.url}</div>
          <div className="flex gap-2 justify-center">
            <button
              onClick={downloadQr}
              className="bg-secondary text-secondary-foreground hover:bg-secondary/85 px-5 py-2.5 rounded-sm inline-flex items-center gap-2 text-xs uppercase tracking-wider"
              data-testid="landing-qr-download"
            >
              <Download className="w-4 h-4" /> Download SVG
            </button>
            <button
              onClick={() => copy(qrFor.url)}
              className="border border-secondary/40 hover:bg-secondary/10 px-5 py-2.5 rounded-sm inline-flex items-center gap-2 text-xs uppercase tracking-wider"
            >
              <Copy className="w-4 h-4" /> Copy link
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}
